import { useState, useEffect } from 'react'
import QRCode from 'react-qr-code'
import { auth } from '../../firebase/config'
import firebase from '../../firebase/compat'
import {
    getModularAuth,
    multiFactor,
    TotpMultiFactorGenerator,
    getMultiFactorResolver,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from '../../firebase/modularAuth'
import { normalizeTotpCode, totpFactor, totpHint, totpMessage, TOTP_ISSUER } from '../../helpers/auth/totp'
import Hostname from '../../hooks/Hostname'
import TwoFactorStatusBadge from './TwoFactorStatusBadge'
import { client } from '../../hooks/Client'

// Het blok 'Tweestapsverificatie' op Mijn profiel: een authenticator-app (TOTP)
// koppelen en ontkoppelen. Nodig omdat het profiel verder alleen naam, foto en
// uitloggen kent, en MFA in de modular API leeft (zie firebase/modularAuth.js).
//
// Twee voorwaarden van Firebase zitten in de stappen: een bevestigd e-mailadres
// (anders eerst `resendVerificationMail`), en een recente inlog voor koppelen
// én ontkoppelen (anders eerst het wachtwoord, en met een gekoppelde app ook de
// code).
// AI-CONTEXT: See docs/features/mfa-totp.md

/** Naam van de factor in Firebase; staat in de lijst van gekoppelde factoren. */
const FACTOR_NAME = 'Authenticator-app'

const ONVOLLEDIGE_CODE = 'Vul de zes cijfers uit je authenticator-app in.'

/** @param {string} name @returns {Function} callable in europe-west1 */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

/**
 * Werkt op de ingelogde gebruiker.
 *
 * @param {object} props
 * @param {() => void} [props.onEnrolled] - Na een geslaagde koppeling; MfaGate logt dan uit zodat de gebruiker met de code inlogt
 */
const TwoFactorSettings = ({ onEnrolled }) => {
    // null = nog onbekend wie er ingelogd is; dan tonen we niets.
    const [known, setKnown] = useState(/** @type {boolean | null} */ (null))
    // Ophogen na elke wijziging aan het account, zodat de render de factoren en
    // emailVerified opnieuw leest (die leven op het SDK-object, niet in state).
    const [, setVersion] = useState(0)
    const refresh = () => setVersion((v) => v + 1)

    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState('')
    const [secret, setSecret] = useState(/** @type {import('firebase/auth').TotpSecret | null} */ (null))
    const [code, setCode] = useState('')
    const [confirmUnenroll, setConfirmUnenroll] = useState(false)
    // Herbevestigen: welke handeling erop wacht, het wachtwoord, en (met een
    // gekoppelde app) de resolver voor de code.
    const [reauthFor, setReauthFor] = useState(/** @type {'enroll' | 'unenroll' | null} */ (null))
    const [password, setPassword] = useState('')
    const [reauthResolver, setReauthResolver] = useState(null)

    const host = Hostname()

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => setKnown(!!user))
        return () => unsub()
    }, [])

    if (!known) return null
    const user = getModularAuth().currentUser
    if (!user) return null
    const factor = totpFactor(multiFactor(user).enrolledFactors)

    /** @param {() => Promise<void>} fn */
    const withBusy = async (fn) => {
        setBusy(true)
        setMessage('')
        try {
            await fn()
        } finally {
            setBusy(false)
        }
    }

    /** @param {'enroll' | 'unenroll'} action */
    const run = (action) => withBusy(async () => {
        try {
            if (action === 'enroll') {
                const session = await multiFactor(user).getSession()
                setSecret(await TotpMultiFactorGenerator.generateSecret(session))
                setCode('')
            } else {
                await multiFactor(user).unenroll(factor)
                setConfirmUnenroll(false)
                setMessage('De authenticator-app is ontkoppeld.')
                refresh()
            }
        } catch (e) {
            if (e?.code === 'auth/requires-recent-login') {
                setReauthFor(action)
                setPassword('')
                return
            }
            // Na ontkoppelen trekt Firebase de sessie in: het is dan wél gelukt.
            if (action === 'unenroll' && e?.code === 'auth/user-token-expired') {
                setMessage('De authenticator-app is ontkoppeld. Log opnieuw in.')
                return
            }
            setMessage(totpMessage(e?.code))
        }
    })

    const finishEnroll = () => {
        const clean = normalizeTotpCode(code)
        if (!clean) {
            setMessage(ONVOLLEDIGE_CODE)
            return
        }
        return withBusy(async () => {
            try {
                const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, clean)
                await multiFactor(user).enroll(assertion, FACTOR_NAME)
                setSecret(null)
                setCode('')
                setMessage('De authenticator-app is gekoppeld.')
                refresh()
                if (onEnrolled) await onEnrolled()
            } catch (e) {
                setMessage(totpMessage(e?.code))
            }
        })
    }

    const afterReauth = () => {
        const pending = reauthFor
        setReauthFor(null)
        setReauthResolver(null)
        setPassword('')
        setCode('')
        return run(pending)
    }

    const submitPassword = () => withBusy(async () => {
        try {
            await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
        } catch (e) {
            if (e?.code === 'auth/multi-factor-auth-required') {
                setReauthResolver(getMultiFactorResolver(getModularAuth(), e))
                setCode('')
                return
            }
            setMessage(totpMessage(e?.code))
            return
        }
        await afterReauth()
    })

    const submitReauthCode = () => {
        const clean = normalizeTotpCode(code)
        const hint = totpHint(reauthResolver?.hints)
        if (!clean) {
            setMessage(ONVOLLEDIGE_CODE)
            return
        }
        return withBusy(async () => {
            try {
                await reauthResolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, clean))
            } catch (e) {
                setMessage(totpMessage(e?.code))
                return
            }
            await afterReauth()
        })
    }

    const sendVerificationMail = () => withBusy(async () => {
        try {
            const result = await callable('resendVerificationMail')({ client, hostname: window.location.hostname })
            const status = result.data.status
            if (status === 'sent') {
                setMessage(`We hebben een verificatiemail gestuurd naar ${user.email}. Klik op de knop in die mail, en daarna hier op 'Ik heb mijn e-mailadres bevestigd'.`)
            } else if (status === 'too-soon') {
                setMessage('Je hebt net al een mail aangevraagd. Probeer het over een minuut opnieuw.')
            } else {
                await user.reload()
                refresh()
            }
        } catch (e) {
            setMessage(`De mail kon niet worden verstuurd: ${e.message}`)
        }
    })

    const checkVerified = () => withBusy(async () => {
        try {
            await user.reload()
            if (!user.emailVerified) {
                setMessage('Je e-mailadres is nog niet bevestigd. Klik eerst op de knop in de verificatiemail.')
                return
            }
            // Het ID-token draagt email_verified ook; zonder verversen weigert
            // Firebase het koppelen met 'auth/unverified-email'.
            await user.getIdToken(true)
            refresh()
        } catch (e) {
            setMessage(totpMessage(e?.code))
        }
    })

    const codeField = (onSubmit) => (
        <>
            <label htmlFor='totp-code-settings'>Code uit je authenticator-app</label>
            <input
                id='totp-code-settings'
                className='input-classic'
                type='text'
                inputMode='numeric'
                autoComplete='one-time-code'
                value={code}
                onChange={(e) => { const value = e.target.value; setCode(value) }}
                onKeyDown={(e) => { if (e.key === 'Enter') onSubmit() }}
            />
        </>
    )

    const body = () => {
        if (reauthFor && reauthResolver) {
            return (
                <>
                    {codeField(submitReauthCode)}
                    <div className='two-factor-actions'>
                        <button onClick={submitReauthCode} disabled={busy}>Bevestigen</button>
                    </div>
                </>
            )
        }
        if (reauthFor) {
            return (
                <>
                    {/* Firebase eist een recente inlog voor (ont)koppelen; zonder
                        deze zin leest de vraag als een fout. */}
                    <p>Voor je veiligheid vragen we je wachtwoord opnieuw voordat je een authenticator-app {reauthFor === 'enroll' ? 'koppelt' : 'ontkoppelt'}.</p>
                    <label htmlFor='totp-reauth-password'>Bevestig je wachtwoord</label>
                    <input
                        id='totp-reauth-password'
                        className='input-classic'
                        type='password'
                        value={password}
                        onChange={(e) => { const value = e.target.value; setPassword(value) }}
                    />
                    <div className='two-factor-actions'>
                        <button onClick={submitPassword} disabled={busy}>Bevestigen</button>
                        <button onClick={() => setReauthFor(null)} disabled={busy}>Annuleren</button>
                    </div>
                </>
            )
        }
        if (factor) {
            return (
                <>
                    <p>Bij het inloggen vraagt {host?.Name || TOTP_ISSUER} na je wachtwoord om een code uit je authenticator-app.</p>
                    {confirmUnenroll ? (
                        <div className='two-factor-actions'>
                            <button onClick={() => run('unenroll')} disabled={busy}>Ja, ontkoppelen</button>
                            <button onClick={() => setConfirmUnenroll(false)} disabled={busy}>Annuleren</button>
                        </div>
                    ) : (
                        <button onClick={() => setConfirmUnenroll(true)} disabled={busy}>Authenticator-app ontkoppelen</button>
                    )}
                </>
            )
        }
        if (!user.emailVerified) {
            return (
                <>
                    <p>Bevestig eerst je e-mailadres; daarna kun je een authenticator-app koppelen.</p>
                    <div className='two-factor-actions'>
                        <button onClick={sendVerificationMail} disabled={busy}>Stuur een verificatiemail</button>
                        <button onClick={checkVerified} disabled={busy}>Ik heb mijn e-mailadres bevestigd</button>
                    </div>
                </>
            )
        }
        if (secret) {
            return (
                <>
                    <p>Scan deze QR-code met je authenticator-app, of voer de sleutel eronder in.</p>
                    <div className='two-factor-qr'>
                        <QRCode value={secret.generateQrCodeUrl(user.email, host?.Name || TOTP_ISSUER)} size={180} />
                    </div>
                    <code className='two-factor-secret'>{secret.secretKey}</code>
                    {codeField(finishEnroll)}
                    <div className='two-factor-actions'>
                        <button onClick={finishEnroll} disabled={busy}>Koppelen</button>
                        <button onClick={() => setSecret(null)} disabled={busy}>Annuleren</button>
                    </div>
                </>
            )
        }
        return (
            <button onClick={() => run('enroll')} disabled={busy}>Authenticator-app koppelen</button>
        )
    }

    return (
        <div className='dashboard-container two-factor-settings'>
            <div className='two-factor-header'>
                <h2>Tweestapsverificatie</h2>
                <TwoFactorStatusBadge on={!!factor} />
            </div>
            {body()}
            {message && <p role='status'>{message}</p>}
        </div>
    )
}

export default TwoFactorSettings
